import { Box, InputAdornment } from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import filterIcon from 'assets/icon/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/icon/searchIcon.svg';
import Button from 'components/Button';
import CheckboxWithLabel from 'components/CheckboxWithLabel';
import DatePicker from 'components/DatePicker';
import { TextFieldStyle } from 'components/TextInput';
import { Field, Form, Formik, FormikValues } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useClickAway, useDebounce } from 'react-use';
import theme from 'style/theme';
import styled from 'styled-components';
import { SearchParams } from './types';

export const FilterStatus = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.radicalRed};
  top: -4px;
  right: -4px;
  position: absolute;
  z-index: 1;
`;

export const SwitchBtn = styled.div<{ showSubmit?: boolean }>`
  && {
    margin-left: 8px;
    display: flex;
    border-radius: 8px;
    border: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    color: ${(props) => props.theme.colors.white};
    cursor: pointer;
    overflow: hidden;
    background-color: ${(props) =>
      props.showSubmit ? props.theme.colors.radicalRed : props.theme.colors.malibu};
    :hover,
    :active,
    :focus {
      background-color: ${(props) =>
        props.showSubmit ? props.theme.colors.radicalRed : props.theme.colors.malibu} !important;
    }
    transition: width 0.3s;
    width: ${(prop) => (prop.showSubmit ? '84px' : '40px')};
    height: 34.15px;
  }
`;

export const ConfirmBox = styled.div<{ showSubmit?: boolean }>`
  display: ${(prop) => (prop.showSubmit ? 'flex' : 'none')};
`;

export const useSearchBarStyles = makeStyles(() => ({
  root: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    textAlign: 'left',
    paddingBottom: 0,
    marginBottom: 8,
    color: theme.colors.eastBay,
    fontWeight: 700,
    justifyContent: 'flex-start',
    '&:hover,&:focus': {
      color: theme.colors.eastBay,
    },
    '& span': {
      width: '100%',
    },
    '& h4': {
      textAlign: 'left',
      fontSize: 18,
      margin: 0,
      padding: 0,
    },
  },
  popper: {
    border: `1px solid ${theme.colors.shadowBorderGrey}`,
    boxShadow: `0 3px 12px ${theme.colors.shadowBorderGrey}`,
    maxWidth: 308,
    width: '100%',
    zIndex: 100,
    fontSize: 14,
    color: theme.colors.eastBay,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    fontFamily: 'Poppins',
    marginTop: '12px !important',
  },
  inputBase: {
    padding: 10,
    width: '100%',
    // borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 8,
      backgroundColor: theme.colors.white,
      padding: '8px 25px',
      border: `1px solid ${theme.colors.ghost}`,
      fontSize: 14,
      '&:focus': {
        boxShadow: `${theme.colors.mainlyblue} 0 0 0 0.2rem`,
        borderColor: theme.colors.malibu06,
      },
    },
  },
  paper: {
    margin: 0,
    color: theme.colors.comet,
    fontSize: 14,
  },
  option: {
    minHeight: 'auto',
    alignItems: 'flex-start',
    padding: 8,
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
      zIndex: 2,
    },
  },
  popperDisablePortal: {
    position: 'relative',
    zIndex: 1,
  },
  iconSelected: {
    width: 17,
    height: 17,
    marginRight: 5,
    marginLeft: -2,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  text: {
    flexGrow: 1,
  },
  close: {
    opacity: 0.6,
    width: 18,
    height: 18,
  },
}));

export const SearchBox = styled(TextFieldStyle)``;

export type SearchBarProps = {
  items?: { label: string; value: string }[] | undefined;
  enableSingleDateFilter?: boolean;
  enableFreeText?: boolean;
  enableFilterButton?: boolean;
  isShowDateFilters?: boolean;
  filterDateFrom?: Date;
  filterDateTo?: Date;
  onFilterChange?: (searchParams: SearchParams) => void;
};

type Values = {
  specificDate?: Date;
  startDate?: Date;
  endDate?: Date;
} & Record<string, boolean>;

const SearchBar: React.VFC<SearchBarProps> = ({
  items,
  onFilterChange,
  enableFreeText = false,
  enableFilterButton = false,
  enableSingleDateFilter = false,
  isShowDateFilters = true,
  filterDateFrom,
  filterDateTo,
}) => {
  const specificDateFormRef = useRef<any>();
  const { t } = useTranslation();
  const classes = useSearchBarStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formValues, setFormValues] = useState<Values>({});
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [specificDate, setSpecificDate] = useState(new Date());
  const clickAwayRef = useRef(null);

  useDebounce(
    () => {
      setSearchValue(searchInput);
    },
    500,
    [searchInput],
  );

  useEffect(() => {
    setFormValues({});
    setSearchInput('');
    setSearchValue('');
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (open) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const onSubmit = (values: FormikValues) => {
    setFormValues(values);
    if (open) {
      handleClose();
    }
  };

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({
        at: specificDate,
        from: formValues?.startDate ?? undefined,
        to: formValues?.endDate ?? undefined,
        checkList: items
          ?.map((item) => (formValues[item.value] ? item.value : null))
          .filter((item) => !!item),
        search: searchValue || undefined,
      });
    }
  }, [searchValue, formValues, specificDate]);

  useClickAway(
    clickAwayRef,
    () => {
      handleClose();
    },
    ['click'],
  );

  const onCancelClick = (event: React.MouseEvent<HTMLElement>) => {
    if (Object.values(formValues).some((val) => !!val)) {
      setFormValues({});
    }
    handleClick(event);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  const hasFilterValue = !isEmpty(
    Object.keys(formValues).reduce((acc, key) => {
      if (formValues[key]) {
        return {
          ...acc,
          [key]: formValues[key],
        };
      } else {
        return acc;
      }
    }, {}),
  );

  return (
    <Box
      ref={clickAwayRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '@media(max-width: 1280px)': {
          flexWrap: 'wrap',
        },
      }}
    >
      {enableFreeText && (
        <SearchBox
          id="search"
          sx={{ m: 0, width: '271px' }}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          size="small"
          value={searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  mr: 1,
                }}
              >
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}

      {enableSingleDateFilter && (
        <Formik
          initialValues={{ specificDate: new Date() }}
          innerRef={specificDateFormRef}
          validateOnChange
          onSubmit={() => console.log()}
        >
          <Form onChange={() => console.log('change')}>
            <Box sx={{ p: '16px' }}>
              <Field
                component={DatePicker}
                onAccept={setSpecificDate}
                name="specificDate"
                label={t('txt_date')}
                editable={true}
                inputFormat="dd.MM.yyyy"
                mask="__.__.____"
                maxDateTime={new Date()}
              />
            </Box>
          </Form>
        </Formik>
      )}

      {enableFilterButton && (
        <SwitchBtn showSubmit={false} style={{ marginLeft: 0, overflow: 'visible' }}>
          <Button $isIcon={true} id="filter-btn" onClick={handleClick}>
            <SVG src={filterIcon} />
            {hasFilterValue ? <FilterStatus /> : null}
          </Button>
          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            className={classes.popper}
          >
            <Box>
              <Formik onSubmit={onSubmit} initialValues={formValues}>
                {({ values, handleSubmit }) => (
                  <Form
                    onSubmit={handleSubmit}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Box sx={{ p: '16px' }}>
                      <Box sx={{ maxHeight: '35vh', overflowY: 'auto' }}>
                        {isShowDateFilters && (
                          <>
                            <Box sx={{ p: '6px 0px', pb: 0 }}>
                              <Field
                                component={DatePicker}
                                name="startDate"
                                label={t('txt_of')}
                                editable={true}
                                inputFormat="dd.MM.yyyy"
                                maxDateTime={values.endDate}
                                value={filterDateFrom}
                                filter
                              />
                            </Box>
                            <Box sx={{ pb: '6px' }}>
                              <Field
                                component={DatePicker}
                                name="endDate"
                                label={t('txt_to')}
                                editable={true}
                                inputFormat="dd.MM.yyyy"
                                minDateTime={values.startDate}
                                value={filterDateTo}
                                filter
                              />
                            </Box>
                          </>
                        )}
                        {items &&
                          items.map((item, index) => (
                            <Box sx={{ pb: '16px' }} key={`filterCheckBox-${index}`}>
                              <Field
                                component={CheckboxWithLabel}
                                name={item.value}
                                label={item.label}
                                type="checkbox"
                                editable={true}
                              />
                            </Box>
                          ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Button id="apply-btn" type="submit">
                          {t('apply_btn')}
                        </Button>
                        <Button id="reset-btn" $isAlert={true} onClick={onCancelClick}>
                          {t('reset_btn')}
                        </Button>
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Popper>
        </SwitchBtn>
      )}
    </Box>
  );
};

export default SearchBar;
